/**
 * The automated minitank (02 §9.2, mission 5). Once the ranger comes within its sensors it
 * never lets go: it drives at him by the shortest way round anything solid - through
 * trenches and minefields alike, it has no idea what either is - rattles off short MG bursts
 * when it has a line, and blows itself up the moment it touches him. No uniform, trench or
 * pond hides him from it. A LAW, a bomb or a mine is the only answer (`hazards/blast.ts`,
 * `hazards/mines.ts`).
 *
 * Fields, for the renderer and audio: `peeking` = awake and hunting; `moving`/`travelled`
 * drive the tracks; `facing` is the hull; `burstLeft > 0` = firing.
 */

import { findPath } from '../../astar';
import { makeNoise } from '../../alert';
import type { Ctx } from '../../ctx';
import { explode } from '../../damage';
import { rangerOnFoot, rangerSubmerged } from '../../hazards/terrain';
import { atan2, cos, PI, sin } from '../../math';
import { spawnBullet } from '../../projectiles';
import { clearSight } from '../../smoke';
import { lineOfSight, resolveMovement } from '../../statics';
import { ENEMY, NOISE, SPECIALS } from '../../tuning';
import { DT, type Enemy } from '../../types';
import { nearestFree, turnToward } from '../movement';
import { destroyOutright, restoreArmour } from './kinds';

const DEG = PI / 180;

function drive(ctx: Ctx, e: Enemy, tx: number, tz: number): void {
  const dx = tx - e.x;
  const dz = tz - e.z;
  const d = Math.sqrt(dx * dx + dz * dz);
  if (d < 1e-4) return;
  const step = Math.min(d, SPECIALS.tankSpeed * DT);
  const moved = resolveMovement(ctx.statics, e.x + (dx / d) * step, e.z + (dz / d) * step, SPECIALS.tankRadius);
  e.travelled += Math.sqrt((moved.x - e.x) * (moved.x - e.x) + (moved.z - e.z) * (moved.z - e.z));
  e.x = moved.x;
  e.z = moved.z;
  e.moving = true;
  turnToward(e, atan2(dx, dz), SPECIALS.tankTurnRate);
}

/** Like the soldiers' `travel`, but on a grid without costs: trenches and mines mean nothing to it. */
function pursue(ctx: Ctx, e: Enemy, x: number, z: number): void {
  const { statics, world: w } = ctx;
  const last = e.path[e.path.length - 1];
  const stale = !last || (last[0] - x) * (last[0] - x) + (last[1] - z) * (last[1] - z) > 9;
  if (stale && w.tick % 4 === e.id % 4) {
    const start = nearestFree(statics, e.x, e.z, 2);
    const goal = nearestFree(statics, x, z, 4);
    const { width, height, blocked } = statics.nav;
    const grid = { width, height, blocked };
    const cells = start && goal ? findPath(grid, start[0], start[1], goal[0], goal[1], 6000) : null;
    e.path = cells ? cells.map(([cx, cz]) => [cx + 0.5, cz + 0.5] as [number, number]) : [];
    e.pathIndex = e.path.length > 1 ? 1 : 0;
  }
  const node = e.path[e.pathIndex];
  if (!node) {
    e.moving = false;
    return;
  }
  if ((node[0] - e.x) * (node[0] - e.x) + (node[1] - e.z) * (node[1] - e.z) < 0.35 * 0.35) e.pathIndex++;
  else drive(ctx, e, node[0], node[1]);
}

export function stepMinitank(ctx: Ctx, e: Enemy): void {
  const { world: w, statics } = ctx;
  restoreArmour(e);
  const r = w.ranger;
  const onFoot = rangerOnFoot(w);
  const dx = r.x - e.x;
  const dz = r.z - e.z;
  const dist = Math.sqrt(dx * dx + dz * dz);
  if (!e.peeking && onFoot && dist <= SPECIALS.tankWakeRadius) e.peeking = true;
  if (!e.peeking || !onFoot) {
    e.moving = false;
    e.burstLeft = 0;
    return;
  }

  if (dist <= SPECIALS.tankContact) {
    // Contact: it is a bomb on tracks. `tankContact` sits inside the blast's two-wound core (02 §5).
    destroyOutright(ctx, e, false);
    // Its own doing: the debrief pays nothing for a machine that spent itself on him (scoring.ts).
    e.selfDestroyed = true;
    explode(ctx, e.x, e.z, SPECIALS.tankBlast, 'grenade', false);
    return;
  }

  // The last few metres it simply drives at him; further out it follows the grid round obstacles.
  const open = dist <= SPECIALS.tankDirectRange && lineOfSight(statics, e.x, e.z, 0.5, r.x, r.z, 0.3);
  if (open) drive(ctx, e, r.x, r.z);
  else pursue(ctx, e, r.x, r.z);

  // A short burst when it has a line: a nuisance more than a threat (the sources disagree on whether
  // it shot at all, so it does both, weakly). Rounds pass over a man crawling in a trench, as any do.
  const hasLine = dist <= SPECIALS.tankFireRange && !rangerSubmerged(w)
    && clearSight(ctx, e.x, e.z, 0.9, r.x, r.z, r.stance === 'crawl' ? 0.4 : 1.2);
  if (!hasLine) {
    e.burstLeft = 0;
    return;
  }
  if (e.fireCooldown > 0) return;
  if (e.burstLeft <= 0) {
    e.burstLeft = SPECIALS.tankBurst;
    e.fireCooldown = SPECIALS.tankPauseTicks;
    return;
  }
  const a = atan2(dx, dz) + (ctx.rng.float() + ctx.rng.float() - 1) * SPECIALS.tankSpreadDeg * DEG;
  spawnBullet(ctx, false, e.x + sin(a) * 0.7, e.z + cos(a) * 0.7, sin(a), cos(a), ENEMY.bulletRange, -1, false);
  e.burstLeft--;
  e.sinceShot = 0;
  e.fireCooldown = SPECIALS.tankTicksPerShot;
  // Its MG is as loud as any rifle.
  makeNoise(ctx, e.x, e.z, NOISE.enemyShot, 'gunfire', e.id);
}
